import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  Fragment,
} from "react"
import { kebabCase } from "lodash"
import { Form } from "react-bootstrap"
import { className as cn } from "@/helpers/className"
import { ReactComponent as Pencil } from "@/assets/images/pencil.svg"
import { buildId } from "@/helpers/forms"
import FormEditor from "@/components/FormEditor"
import { sanitizeHtml } from "@/helpers/htmlUtils"
export const NodeTypes = Object.freeze({
  Textarea: "textarea",
  Input: "input",
  Editor: "editor",
})

const getInputType = type => {
  if ([NodeTypes.Textarea, NodeTypes.Input, NodeTypes.Editor].includes(type)) {
    return type
  }
  return NodeTypes.Textarea
}

const LoadingSpinner = () => <div className="spinner-border text-primary" />

const SaveButton = ({ onClick, disabled, placeholder }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="partial-entity-text-add-btn"
    data-test={`save-${kebabCase(placeholder)}`}
  >
    Save
  </button>
)

const EditButton = ({ onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} className="icon-16-px btn-clear text-black">
    <Pencil />
  </button>
)

const AddButton = ({ onClick, disabled, placeholder, label, className }) => (
  <button
    disabled={disabled}
    className={cn("partial-entity-text-add-btn w-100", className)}
    onClick={onClick}
    data-test={`open-${kebabCase(placeholder || label)}`}
  >
    {placeholder || label}
  </button>
)

const PartialEntityTextUpdate = forwardRef(function PartialEntityTextUpdate(
  {
    label,
    placeholder,
    nodeType = NodeTypes.Textarea,
    inputProps,
    disabled,
    name,
    value: val,
    readonly,
    loading,
    readonlyPlaceholder = "",
    initialEditState = false,
    onChange,
    onCancel = () => {},
    className,
    initialButtonClassName,
    rows,
  },
  ref
) {
  const inputRef = useRef(null)
  const timerRef = useRef(null)
  const [editable, setEditable] = useState(initialEditState)
  const [value, setValue] = useState(val || "")
  const inputId = useMemo(buildId, [])
  const inputDataTest = `${nodeType}-${kebabCase(placeholder)}`

  const inputPropsWithDataTest = useMemo(
    () => ({
      "data-test": inputDataTest,
      ...inputProps,
    }),
    [inputDataTest, inputProps]
  )

  const updateValue = useCallback(
    event => {
      const newValue = nodeType === NodeTypes.Editor ? event : event.target.value
      setValue(newValue)
    },
    [nodeType]
  )

  const updateEditable = useCallback(
    value => () => {
      setEditable(value)
      if (value) {
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
          if (inputRef.current) inputRef.current.focus()
        }, 4)
      }
    },
    []
  )

  const onSaveClick = useCallback(async () => {
    try {
      if (!value || value.trim().length === 0) {
        throw new Error("Value cannot be empty")
      }

      const trimmedValue = value.trim()
      const dataToUpdate = name ? { [name]: trimmedValue } : trimmedValue

      await onChange(dataToUpdate)
      updateEditable(false)()
    } catch (error) {
      console.error("Error saving changes:", error)
    }
  }, [value, name, onChange, updateEditable])

  const onKeyDown = useCallback(
    event => {
      if (nodeType === NodeTypes.Editor) return

      switch (event.key) {
        case "Enter":
          if (event.ctrlKey || event.metaKey) {
            onSaveClick()
          }
          break
        case "Escape":
          updateEditable(false)()
          onCancel()
          break
        default:
          return
      }
    },
    [nodeType, onSaveClick, updateEditable, onCancel]
  )

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        inputRef.current?.focus()
      },
    }),
    []
  )

  useEffect(() => {
    if (!loading && !editable && val !== value) {
      setValue(val || "")
    }
  }, [val, value, editable, loading])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const renderEditableContent = useCallback(() => {
    if (nodeType === NodeTypes.Editor) {
      return <FormEditor ref={inputRef} value={value} onChange={updateValue} dataTest={inputDataTest} />
    }

    return (
      <Form.Control
        className="v-2"
        id={inputId}
        as={getInputType(nodeType)}
        ref={inputRef}
        value={value}
        onKeyDown={onKeyDown}
        onChange={updateValue}
        placeholder={placeholder}
        disabled={loading || disabled}
        rows={rows}
        {...inputPropsWithDataTest}
      />
    )
  }, [
    nodeType,
    inputId,
    value,
    onKeyDown,
    updateValue,
    placeholder,
    loading,
    disabled,
    rows,
    inputDataTest,
    inputPropsWithDataTest,
  ])

  const renderReadonlyContent = useCallback(() => {
    if (nodeType === NodeTypes.Editor) {
      return (
        <div
          className={cn("partial-entity-text-readonly", readonly ? "py-2" : "")}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(val || readonlyPlaceholder) }}
        />
      )
    }

    return (
      <div className={cn("partial-entity-text-readonly", readonly ? "py-2" : "")}>
        {val
          ? String(val)
              .split("\n")
              .map((text, idx, arr) => (
                <Fragment key={text + idx}>
                  {text}
                  {idx !== arr.length - 1 && <br />}
                </Fragment>
              ))
          : readonlyPlaceholder}
      </div>
    )
  }, [nodeType, val, readonlyPlaceholder, readonly])

  const Grid = useCallback(
    ({ children, isEditor }) => (
      <div className="d-flex flex-column w-100 mt-n1">
        {label && (
          <label className="mb-0 text-black-50" htmlFor={inputId}>
            {label}
          </label>
        )}
        <div className={cn("d-flex", isEditor ? "gap-1 flex-column" : "gap-2 flex-no-wrap", className)}>
          <div className={isEditor ? "w-100" : "flex-grow-1"}>{children?.content}</div>
          <div className={cn("d-flex justify-content-end", isEditor ? "w-100" : "flex-shrink-0")}>
            {children?.action}
          </div>
        </div>
      </div>
    ),
    [className, label, inputId]
  )

  if (readonly) return renderReadonlyContent()

  if (editable) {
    return (
      <Grid isEditor={nodeType === NodeTypes.Editor}>
        {{
          content: renderEditableContent(),
          action: loading ? (
            <LoadingSpinner />
          ) : (
            <SaveButton onClick={onSaveClick} disabled={disabled} placeholder={placeholder} />
          ),
        }}
      </Grid>
    )
  }

  if (!value) {
    return (
      <AddButton
        onClick={updateEditable(true)}
        disabled={disabled}
        placeholder={placeholder}
        label={label}
        className={initialButtonClassName}
      />
    )
  }

  return (
    <Grid isEditor={editable && nodeType === NodeTypes.Editor}>
      {{
        content: renderReadonlyContent(),
        action: loading ? <LoadingSpinner /> : <EditButton onClick={updateEditable(true)} disabled={disabled} />,
      }}
    </Grid>
  )
})

PartialEntityTextUpdate.displayName = "PartialEntityTextUpdate"

export default PartialEntityTextUpdate
